import { FilterAction } from '../../../_shared/types/filter/filter';
import { type GroupingColumnValues } from '../../../_shared/types/grouping/grouping';
import { type SpreadsheetColumnId } from '../../../_shared/types/spreadsheetData/spreadsheetColumn';
import { FilterConjunction } from '../../../sidebar/sidebarApps/mapTools/filterTool/filters/filterConjunction.enum';
import { DataType } from '../../spreadsheetData/spreadsheetData.state';
import { type MapSettingsDataAction } from '../data/mapSettingsData.action';
import { MAP_SETTINGS_REMOVE_ACTIVE_ITEMS } from '../data/mapSettingsData.actionTypes';
import { type MapSettingsColumnsFilterAction } from './mapSettingsColumnsFilter.action';
import { mapSettingsColumnsFilterSetIncludeNotADate } from './mapSettingsColumnsFilter.actionCreators';
import {
  MAP_SETTINGS_COLUMNS_FILTER_CHANGE_ATTRIBUTE_CONJUNCTION,
  MAP_SETTINGS_COLUMNS_FILTER_CHANGE_TEXT_CONJUNCTION,
  MAP_SETTINGS_COLUMNS_FILTER_CLEAR_FILTERS,
  MAP_SETTINGS_COLUMNS_FILTER_REMOVE_FILTER_VALUES,
  MAP_SETTINGS_COLUMNS_FILTER_SET_ATTRIBUTE,
  MAP_SETTINGS_COLUMNS_FILTER_SET_DATE_FROM,
  MAP_SETTINGS_COLUMNS_FILTER_SET_DATE_TO, MAP_SETTINGS_COLUMNS_FILTER_SET_GROUP,
  MAP_SETTINGS_COLUMNS_FILTER_SET_NUMBER,
  MAP_SETTINGS_COLUMNS_FILTER_SET_TEXT,
} from './mapSettingsColumnsFilter.actionTypes';
import {
  isAttributeColumnsFilterEmpty,
  isGroupColumnsFilterEmpty, isTextColumnsFilterEmpty,
} from './mapSettingsColumnsFilter.helpers';
import {
  type MapSettingsColumnsFilterState,
  type MapSettingsFilterAttributeState,
  type MapSettingsFilterDateState,
  type MapSettingsFilterNumberState,
  type MapSettingsFilterTextState,
} from './mapSettingsColumnsFilter.state';

export const mapSettingsColumnsFilterInitialState: MapSettingsColumnsFilterState = {
};

type DataTypeToFilterStateGeneric<T, S> = {
  type: T;
  value: S;
};

type DataTypeToFilterState =
  DataTypeToFilterStateGeneric<DataType.TEXT, MapSettingsFilterTextState> |
  DataTypeToFilterStateGeneric<DataType.ATTRIBUTE, MapSettingsFilterAttributeState> |
  DataTypeToFilterStateGeneric<DataType.NUMBER, MapSettingsFilterNumberState> |
  DataTypeToFilterStateGeneric<DataType.GROUP, GroupingColumnValues<1>> |
  DataTypeToFilterStateGeneric<DataType.DATE, MapSettingsFilterDateState>;

const addFilterToState = (currentState: MapSettingsColumnsFilterState,
  spreadsheetColumnId: SpreadsheetColumnId, value: DataTypeToFilterState
): MapSettingsColumnsFilterState => {
  return {
    ...currentState,
    [spreadsheetColumnId.spreadsheetId]: {
      ...currentState?.[spreadsheetColumnId.spreadsheetId],
      [spreadsheetColumnId.columnId]: {
        ...currentState?.[spreadsheetColumnId.spreadsheetId]?.[spreadsheetColumnId.columnId],
        [value.type]: value.value,
      },
    },
  };
};

const removeFilterOnColumn = (state: MapSettingsColumnsFilterState, spreadsheetColumnId: SpreadsheetColumnId) => {
  const newState = {
    ...state,
    [spreadsheetColumnId.spreadsheetId]: {
      ...state[spreadsheetColumnId.spreadsheetId],
      [spreadsheetColumnId.columnId]: {
        ...state[spreadsheetColumnId.spreadsheetId]?.[spreadsheetColumnId.columnId],
      },
    },
  };

  delete newState[spreadsheetColumnId.spreadsheetId]?.
    [spreadsheetColumnId.columnId];

  return newState;
};

export const getFilterConjunction = (
  filterState: MapSettingsColumnsFilterState,
  spreadsheetColumnId: SpreadsheetColumnId,
  dataType: DataType.ATTRIBUTE | DataType.TEXT
): FilterConjunction => {
  const data = filterState[spreadsheetColumnId.spreadsheetId]?.[spreadsheetColumnId.columnId]?.[dataType];

  return data?.conjunction ?? FilterConjunction.And;
};

export const mapSettingsColumnsFilterReducer = (state = mapSettingsColumnsFilterInitialState,
  action: MapSettingsColumnsFilterAction | MapSettingsDataAction): MapSettingsColumnsFilterState => {

  if (mapSettingsColumnsFilterSetIncludeNotADate.match(action)) {
    const currentDateFilter = state[action.payload.spreadsheetColumnId.spreadsheetId]
      ?.[action.payload.spreadsheetColumnId.columnId]?.[DataType.DATE];

    if (currentDateFilter) {
      return addFilterToState(state, action.payload.spreadsheetColumnId, {
        type: DataType.DATE,
        value: {
          ...currentDateFilter,
          includeNotADate: action.payload.newValue,
        },
      });
    }
  }

  switch (action.type) {
    case MAP_SETTINGS_COLUMNS_FILTER_CLEAR_FILTERS: {
      return mapSettingsColumnsFilterInitialState;
    }

    case MAP_SETTINGS_COLUMNS_FILTER_REMOVE_FILTER_VALUES: {
      return removeFilterOnColumn(state, action.payload.spreadsheetColumnId);
    }

    case MAP_SETTINGS_COLUMNS_FILTER_SET_TEXT: {
      const spreadsheetColumnId = action.payload.spreadsheetColumnId;

      const newState = addFilterToState(state, spreadsheetColumnId, {
        type: DataType.TEXT,
        value: {
          conjunction: getFilterConjunction(state, spreadsheetColumnId, DataType.TEXT),
          values: action.payload.values,
        },
      });

      if (isTextColumnsFilterEmpty(newState, spreadsheetColumnId)) {
        return removeFilterOnColumn(newState, spreadsheetColumnId);
      }

      return newState;
    }

    case MAP_SETTINGS_COLUMNS_FILTER_CHANGE_TEXT_CONJUNCTION: {
      const value: MapSettingsFilterTextState = {
        conjunction: action.payload.conjunction,
        values: state[action.payload.spreadsheetColumnId.spreadsheetId]
          ?.[action.payload.spreadsheetColumnId.columnId]?.[DataType.TEXT]?.values ?? [],
      };

      return addFilterToState(state, action.payload.spreadsheetColumnId, {
        type: DataType.TEXT,
        value,
      });
    }

    case MAP_SETTINGS_COLUMNS_FILTER_SET_GROUP: {
      const spreadsheetColumnId = action.payload.spreadsheetColumnId;

      const newState = addFilterToState(state, spreadsheetColumnId, {
        type: DataType.GROUP,
        value: action.payload.group,
      });

      if (isGroupColumnsFilterEmpty(newState, spreadsheetColumnId)) {
        return removeFilterOnColumn(newState, spreadsheetColumnId);
      }

      return newState;
    }

    case MAP_SETTINGS_COLUMNS_FILTER_SET_NUMBER: {
      const value: MapSettingsFilterNumberState = {
        values: {
          action: FilterAction.Range,
          value: [action.payload.range.from, action.payload.range.to],
        },
      };

      return addFilterToState(state, action.payload.spreadsheetColumnId, {
        type: DataType.NUMBER,
        value,
      });
    }

    case MAP_SETTINGS_COLUMNS_FILTER_SET_DATE_FROM: {
      const currentDateFilter = state[action.payload.spreadsheetColumnId.spreadsheetId]
        ?.[action.payload.spreadsheetColumnId.columnId]?.[DataType.DATE];
      const dateTo = currentDateFilter?.values[1] ?? null;

      const value: MapSettingsFilterDateState = {
        ...currentDateFilter,
        values: [action.payload.dateFrom, dateTo],
      };

      return addFilterToState(state, action.payload.spreadsheetColumnId, {
        type: DataType.DATE,
        value,
      });
    }

    case MAP_SETTINGS_COLUMNS_FILTER_SET_DATE_TO: {
      const currentDateFilter = state[action.payload.spreadsheetColumnId.spreadsheetId]
        ?.[action.payload.spreadsheetColumnId.columnId]?.[DataType.DATE];
      const dateFrom = currentDateFilter?.values[0] ?? null;

      const value: MapSettingsFilterDateState = {
        ...currentDateFilter,
        values: [dateFrom, action.payload.dateTo],
      };

      return addFilterToState(state, action.payload.spreadsheetColumnId, {
        type: DataType.DATE,
        value,
      });
    }

    case MAP_SETTINGS_COLUMNS_FILTER_SET_ATTRIBUTE: {
      const spreadsheetColumnId = action.payload.spreadsheetColumnId;

      const value: MapSettingsFilterAttributeState = {
        values: action.payload.selectedAttributeIds,
        conjunction: getFilterConjunction(state, spreadsheetColumnId, DataType.ATTRIBUTE),
      };

      const newState = addFilterToState(state, spreadsheetColumnId, {
        type: DataType.ATTRIBUTE,
        value,
      });

      if (isAttributeColumnsFilterEmpty(newState, spreadsheetColumnId)) {
        return removeFilterOnColumn(newState, spreadsheetColumnId);
      }

      return newState;
    }

    case MAP_SETTINGS_COLUMNS_FILTER_CHANGE_ATTRIBUTE_CONJUNCTION: {
      const value: MapSettingsFilterAttributeState = {
        conjunction: action.payload.conjunction,
        values: state[action.payload.spreadsheetColumnId.spreadsheetId]?.
          [action.payload.spreadsheetColumnId.columnId]?.[DataType.ATTRIBUTE]?.values ?? {},
      };

      return addFilterToState(state, action.payload.spreadsheetColumnId, {
        type: DataType.ATTRIBUTE,
        value,
      });
    }

    case MAP_SETTINGS_REMOVE_ACTIVE_ITEMS: {
      return mapSettingsColumnsFilterInitialState;
    }

    default:
      return state;
  }
};
